import * as React from 'react';

import Item, { Props as ItemProps } from './Item';

import { Title } from '..';
import cn from 'clsx';
import styles from '@sicredi/styles/_list.scss';

export interface Schema
  extends Pick<
    React.HTMLAttributes<HTMLUListElement>,
    Exclude<keyof React.HTMLAttributes<HTMLUListElement>, 'onChange'>
  > {
    children?: React.ReactNode;
  onChange?: (expanded: number[]) => void;
}

export interface AriaLabelProps extends Schema {
  'aria-label': string;
}

export interface AriaLabelledbybyProps extends Schema {
  'aria-labelledby': string;
}

export type Props = AriaLabelProps | AriaLabelledbybyProps;

export interface CompoundedList extends React.FC<Props> {
  Item: React.FC<ItemProps>;
}

const List: CompoundedList = ({ children, onChange, className, ...props }) => {
  const contents = React.Children.toArray(children) as React.ReactElement<
    ItemProps
  >[];

  const expanded = contents
    .map(({ props }, i) => (props.expanded ? i : -1))
    .filter(i => i !== -1);

  const [internalExpanded, setInternalExpanded] = React.useState<number[]>(
    expanded
  );

  function enhanceClick(i: number, onClick: ItemProps['onClick']) {
    return () => {
      const nextInternalExpanded =
        internalExpanded.find(item => item === i) === undefined
          ? [...internalExpanded, i]
          : internalExpanded.filter(item => item !== i);

      setInternalExpanded(nextInternalExpanded);

      onClick && onClick(i);

      onChange && onChange(expanded.length ? expanded : nextInternalExpanded);
    };
  }

  return (
    <div className={cn(styles['sicredi-list'], className)}>
      {props['aria-label'] && (
        <Title as="h5" weight="semi-bold" className={styles['title']}>
          {props['aria-label']}
        </Title>
      )}
      <ul role="group" {...props}>
        {contents.map((component, i: number) =>
          React.cloneElement(component, {
            onClick: enhanceClick(i, component.props.onClick),
            expanded: expanded.length
              ? component.props.expanded
              : internalExpanded.find(item => item === i) !== undefined,
          })
        )}
      </ul>
    </div>
  );
};

List.Item = Item;

export default List;
